import { supabase } from '@/lib/supabase/client';
import { normalizeOutreachSuppressionInsert } from '@/lib/outreach-safety/suppression-rules';
import {
  outreachSuppressionListSuccessSchema,
  outreachSuppressionMatchesSuccessSchema,
  outreachSuppressionMutationSuccessSchema,
} from '@/lib/outreach-safety/suppression-contract';
import type {
  LeadOutreachSafetySummary,
  OutreachSuppressionInsert,
  OutreachSuppressionMatch,
  OutreachSuppressionReason,
  OutreachSuppressionSafe,
  OutreachSuppressionSource,
} from '@/types/leads';

export const outreachSuppressionReasons: OutreachSuppressionReason[] = [
  'do_not_contact',
  'unsubscribe',
  'hard_bounce',
  'spam_complaint',
  'invalid_email',
  'current_customer',
  'existing_customer',
  'open_deal',
  'inbound_dup',
  'manual_block',
  'other',
];

export const outreachSuppressionSources: OutreachSuppressionSource[] = [
  'manual',
  'mailmeteor',
  'resend',
  'gdrive',
  'admin',
  'import',
  'other',
];

function nullableText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

function normalizeMatch(row: Record<string, unknown>): OutreachSuppressionMatch {
  const matchType = row.match_type === 'email' || row.match_type === 'domain' || row.match_type === 'license_number'
    ? row.match_type
    : 'unknown';
  return {
    lead_id: String(row.lead_id ?? ''),
    suppression_id: String(row.suppression_id ?? ''),
    reason: outreachSuppressionReasons.includes(row.reason as OutreachSuppressionReason)
      ? (row.reason as OutreachSuppressionReason)
      : null,
    source: outreachSuppressionSources.includes(row.source as OutreachSuppressionSource)
      ? (row.source as OutreachSuppressionSource)
      : null,
    notes: nullableText(row.notes as string | null),
    metadata: row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? (row.metadata as Record<string, unknown>)
      : {},
    created_at: nullableText(row.created_at as string | null) ?? '',
    match_type: matchType,
  };
}

export function normalizeSafetySummary(row: Record<string, unknown>): LeadOutreachSafetySummary {
  return {
    lead_id: String(row.lead_id ?? ''),
    active_suppression_count: typeof row.active_suppression_count === 'number'
      ? row.active_suppression_count
      : Number(row.active_suppression_count ?? 0),
    primary_suppression_reason: outreachSuppressionReasons.includes(row.primary_suppression_reason as OutreachSuppressionReason)
      ? (row.primary_suppression_reason as OutreachSuppressionReason)
      : null,
    active_suppression_reasons: nullableText(row.active_suppression_reasons as string | null),
    active_suppression_sources: nullableText(row.active_suppression_sources as string | null),
    latest_suppression_at: nullableText(row.latest_suppression_at as string | null),
  };
}

function validateInsert(input: OutreachSuppressionInsert): OutreachSuppressionInsert {
  if (!outreachSuppressionReasons.includes(input.reason)) {
    throw new Error('Choose a valid do-not-touch reason.');
  }
  if (!outreachSuppressionSources.includes(input.source)) {
    throw new Error('Choose a valid do-not-touch source.');
  }
  return normalizeOutreachSuppressionInsert(input);
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error('Sign in again before changing the do-not-touch list.');
  }
  return data.session.access_token;
}

async function requestJson(path: string, init: RequestInit = {}): Promise<unknown> {
  const accessToken = await getAccessToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(init.headers || {}),
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error?.message || `Outreach suppression request failed (${response.status}).`);
  }
  return payload;
}

export const outreachSuppressionService = {
  async list(options: { includeReleased?: boolean } = {}): Promise<OutreachSuppressionSafe[]> {
    const params = new URLSearchParams();
    if (options.includeReleased) params.set('includeReleased', 'true');
    const path = `/api/outreach-suppressions${params.toString() ? `?${params.toString()}` : ''}`;
    const payload = outreachSuppressionListSuccessSchema.parse(await requestJson(path));
    return payload.suppressions as OutreachSuppressionSafe[];
  },

  async create(input: OutreachSuppressionInsert): Promise<OutreachSuppressionSafe> {
    const insert = validateInsert(input);
    const payload = outreachSuppressionMutationSuccessSchema.parse(await requestJson('/api/outreach-suppressions', {
      method: 'POST',
      body: JSON.stringify(insert),
    }));
    return payload.suppression;
  },

  async release(id: string, releaseReason: string): Promise<OutreachSuppressionSafe> {
    const payload = outreachSuppressionMutationSuccessSchema.parse(await requestJson('/api/outreach-suppressions', {
      method: 'PATCH',
      body: JSON.stringify({ id, releaseReason }),
    }));
    return payload.suppression;
  },

  async listMatchesByLeadId(leadId: string): Promise<OutreachSuppressionMatch[]> {
    const payload = outreachSuppressionMatchesSuccessSchema.parse(await requestJson(
      `/api/outreach-suppressions?leadId=${encodeURIComponent(leadId)}`
    ));
    return payload.matches.map((match) => normalizeMatch(match));
  },
};
